import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const solutions = [
  {
    key: "erp",
    icon: "bx bx-buildings",
    route: "/erp-vallalatiranyitasi-rendszer",
    title: "Happy ERP",
    kicker: "Vállalatirányítás",
    text: "Beszerzés, raktár, pénzügy, gyártás és riportok egy egységes, valós idejű rendszerben.",
    tags: ["BI riportok", "Raktár", "Pénzügy"],
  },
  {
    key: "crm",
    icon: "bx bx-user-voice",
    route: "/crm-rendszer",
    title: "Happy CRM",
    kicker: "Ügyfélkapcsolatok",
    text: "Leadek, ügyfelek, kampányok, naptárak és értékesítési pipeline átlátható, automatizált működéssel.",
    tags: ["Pipeline", "Kampányok", "Naptár"],
  },
  {
    key: "trade",
    icon: "bx bx-store-alt",
    route: "/trade-kereskedelmi-rendszer",
    title: "Happy Trade",
    kicker: "Kereskedelem",
    text: "Webshop, POS, B2B értékesítés, készlet, logisztika és számlázás egyetlen platformon.",
    tags: ["POS", "Webshop", "B2B"],
  },
  {
    key: "gastro",
    icon: "bx bx-restaurant",
    route: "/gastro-ettermi-szoftver",
    title: "Happy Gastro",
    kicker: "Vendéglátás",
    text: "Éttermi rendeléskezelés, asztaltérkép, házhozszállítás, mobil pincér, KDS és NTAK támogatás.",
    tags: ["KDS", "NTAK", "Mobil pincér"],
  },
  {
    key: "service",
    icon: "bx bx-calendar-check",
    route: "/service-idopontfoglalo-app",
    title: "Happy Service App",
    kicker: "Szolgáltatók",
    text: "Online időpontfoglalás, ügyfélkártyák, emlékeztetők, munkalapok és visszatérő ügyfélélmény.",
    tags: ["Foglalás", "Emlékeztetők", "Munkalap"],
  },
  {
    key: "logistic",
    icon: "bx bx-package",
    route: "/logistic-app",
    title: "Happy Logistic App",
    kicker: "Logisztika",
    text: "Szállítási folyamatok, csomagállapotok, futárfeladatok és raktári mozgások valós idejű követése.",
    tags: ["Csomagok", "Futár", "Raktár"],
  },
];

const metrics = [
  { value: "20+", label: "modul és üzleti terület" },
  { value: "24/7", label: "felhő alapú hozzáférés" },
  { value: "API", label: "integrációbarát működés" },
  { value: "HU", label: "magyar fejlesztés és támogatás" },
];

const PremiumHomeSections = () => {
  const { t } = useTranslation();

  return (
    <>
      <section className="premium-section premium-section--compact premium-trust-strip" aria-label="Happy Solutions előnyök">
        <div className="premium-container">
          <div className="premium-trust-grid">
            {metrics.map((metric) => (
              <div className="premium-metric-card" key={metric.label}>
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="premium-section premium-suite" id="megoldasok">
        <div className="premium-container">
          <div className="premium-section-head premium-section-head--split">
            <div>
              <span className="premium-eyebrow">{t("premium.home.suiteEyebrow", { defaultValue: "Moduláris szoftver ökoszisztéma" })}</span>
              <h2>{t("premium.home.suiteTitle", { defaultValue: "Egy rendszer, több iparág, kevesebb napi káosz." })}</h2>
            </div>
            <p>
              {t("premium.home.suiteText", {
                defaultValue:
                  "A Happy Solutions megoldásai ugyanarra az üzleti logikára épülnek: gyors bevezetés, valós idejű adatok, integrációk és átlátható működés a vezetőknek és a napi csapatoknak is.",
              })}
            </p>
          </div>

          <div className="premium-card-grid premium-card-grid--3">
            {solutions.map((solution) => (
              <Link className="premium-solution-card" to={solution.route} key={solution.key}>
                <span className="premium-card-icon" aria-hidden="true">
                  <i className={solution.icon}></i>
                </span>
                <span className="premium-card-kicker">{solution.kicker}</span>
                <h3>{solution.title}</h3>
                <p>{solution.text}</p>
                <span className="premium-chip-row">
                  {solution.tags.map((tag) => (
                    <span className="premium-chip" key={tag}>{tag}</span>
                  ))}
                </span>
                <span className="premium-card-link">
                  {t("common.viewModule", { defaultValue: "Modul megtekintése" })}
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="premium-section premium-process">
        <div className="premium-container premium-process__inner">
          <div className="premium-section-head">
            <span className="premium-eyebrow">{t("premium.home.processEyebrow", { defaultValue: "Bevezetés kompromisszumok nélkül" })}</span>
            <h2>{t("premium.home.processTitle", { defaultValue: "Nem csak szoftvert adunk — működő üzleti rendszert építünk." })}</h2>
          </div>
          <div className="premium-process-list">
            {[
              ["01", "Folyamatfelmérés", "Megértjük a jelenlegi működést, a szűk keresztmetszeteket és a célokat."],
              ["02", "Modulok és integrációk", "Összeállítjuk a megfelelő ERP, CRM, Trade, Gastro, Service vagy Logistic kombinációt."],
              ["03", "Bevezetés és oktatás", "Adatmigráció, jogosultságok, tréningek és támogatott indulás egy kézben."],
              ["04", "Mérés és optimalizálás", "Dashboardok, riportok és automatizmusok segítik a folyamatos fejlődést."],
            ].map(([step, title, text]) => (
              <div className="premium-process-step" key={step}>
                <span>{step}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="premium-section premium-cta-band">
        <div className="premium-container premium-cta-band__inner">
          <div>
            <span className="premium-eyebrow premium-eyebrow--light">{t("premium.home.ctaEyebrow", { defaultValue: "Következő lépés" })}</span>
            <h2>{t("premium.home.ctaTitle", { defaultValue: "Nézzük meg, melyik Happy rendszer gyorsítaná fel a vállalkozásod." })}</h2>
            <p>{t("premium.home.ctaText", { defaultValue: "Kérj bemutatót vagy ingyenes konzultációt — segítünk megtalálni a legjobb modul-összeállítást." })}</p>
          </div>
          <div className="premium-cta-actions">
            <Link className="edu-btn" to="/foglalas">
              {t("tradePage.hero.ctaConsult", { defaultValue: "Ingyenes konzultáció" })}
              <i className="icon-arrow-right-line-right"></i>
            </Link>
            <Link className="premium-text-link" to="/kapcsolat/ugyfelszolgalat">
              {t("nav.support", { defaultValue: "Kérjen ingyenes bemutatót!" })}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default PremiumHomeSections;

